import GroupIcon from '@mui/icons-material/Group';
import WhereToVoteIcon from '@mui/icons-material/WhereToVote';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { Badge, Button } from '@mui/material';
import numeral from 'numeral';
import { useContext, useMemo, useState } from 'react';
import { updateOrganisation } from '../api/organisations';
import { AccountContext } from '../context/account';
import { CreateOrganisationDetails } from '../core/global';
import { companyShape } from '../data/form-shapes';
import { findOrganisation, getPurchaseRequestsByType } from '../helpers/core';
import styles from "../styles/components/user-settings-modal.module.scss";
import { CreateForm } from './form-components';
import { ManagePrivateValidationsModal } from './manage-private-validations-modal';
import { OrganisationUsersModal } from './organisation-users-modal';

interface UserSettingsModalProps {
  handleClose: () => void;
  onUpdateOrganisationId?: (id: string | number | null) => void;
  id: number | string | null;
}

const blankOrganisation: CreateOrganisationDetails = {
  type: 'company',
  name: '',
  city: '',
  postcode: '',
  country: '',
  address: '',
  address_2: '',
  address_3: '',
  vat_number: '',
  role: 'admin',
  features: {}
}

export const OrganisationSettingsModal = ({ id: currentOrganisationId, handleClose, onUpdateOrganisationId }: UserSettingsModalProps) => {
  const [showCompanyUsers, setShowCompanyUsers] = useState(false)
  const [showPrivateValidations, setShowPrivateValidations] = useState(false)
  
  const { 
    account, 
    refetchUserDetails,
    companyPurchaseRequests,
    setShowApprovePurchaseModal,
    userCompanyTokens,
  } = useContext(AccountContext);

  const currentOrganisation: null | CreateOrganisationDetails = useMemo(() => {
    if (currentOrganisationId === 0) {
      return blankOrganisation
    }
    if (currentOrganisationId) {
      const found = findOrganisation(account?.organisation, currentOrganisationId)
      return found ? found : null
    }
    return null
  }, [currentOrganisationId, account])

  const Intro = useMemo(() => {
      if (currentOrganisationId === 0) {
        return `You can create a new organisation. Once you have created your organisation, you can add other users to it. You will automatically be added as **admin**.`
      }
      return `You can update the organization's details, as well as add or remove users as needed.  
Your organisation id is **#${currentOrganisationId}**.  
`
    }, [currentOrganisationId])

    const companyActiveRequests = useMemo(() => {
      return companyPurchaseRequests && currentOrganisationId ? getPurchaseRequestsByType(companyPurchaseRequests, Number(currentOrganisationId), ['pending', 'processing']) : []
    }, [companyPurchaseRequests, currentOrganisationId])

    // Modify the company shape to show the number of unspent tokens
    const useCommpanyShape = useMemo(() => {
      if (!currentOrganisation || !userCompanyTokens) {
        return [...companyShape]
      }
      const foundCompanyTokens = userCompanyTokens.find(token => token.company_id === currentOrganisationId)
      const found = companyShape.find(shape => shape.key === 'super_subscriber')
      if (found && foundCompanyTokens) {
        return [
          ...companyShape.filter(shape => shape.key !== 'super_subscriber'),
          {
            ...found,
            note: found.note + ` You have ${numeral(foundCompanyTokens.tokens_value).format("0,0")} unspent tokens.`
          }
        ]
      }
      return [
        ...companyShape
      ]
    }, [currentOrganisation])

    return (
      <>
        <CreateForm
          title={currentOrganisationId === 0 ? 'Create a new organisation': 'Your organisation'}
          intro={Intro}
          isModal
          shape={useCommpanyShape}
          successMessage={"Your company details have been updated."}
          defaultValues={currentOrganisation}
          onCancel={() => {
            handleClose()
          }}
          onError={(error: any) => {
            refetchUserDetails()
          }}
          onSuccess={(response: any) => {
            if (response && onUpdateOrganisationId) {
              const {updatedOrganisation: {id}} = response
              onUpdateOrganisationId(id)
            }
            refetchUserDetails()
          }}
          onSubmit={(vals) => {
            return updateOrganisation(vals)
          }}
        >
          <>
          {companyActiveRequests && companyActiveRequests.length > 0 ?
            <div style={{marginBottom: "16px"}}>
              <p>You have <strong>{numeral(companyActiveRequests.length).format("0,0")}</strong> purchase requests from other users.</p>
              <Button
                variant="contained"
                // color="success"
                startIcon={<WorkHistoryIcon sx={{color: "#fff"}} />}
                onClick={() => {
                  setShowApprovePurchaseModal(currentOrganisationId)
                }}
              >
                <Badge
                  className={styles.badge}
                  badgeContent={numeral(companyActiveRequests.length).format("0,0")} 
                  color="error"
                >
                  Show purchase requests
                </Badge>
              </Button>
            </div>
            :
            <>
              {currentOrganisationId !== 0 &&
                <div style={{marginBottom: "16px"}}>
                  <p>There are no active purchase requests.</p>
                  <Button
                    variant="contained"
                    // color="success"
                    startIcon={<WorkHistoryIcon sx={{color: "#fff"}} />}
                    onClick={() => {
                      setShowApprovePurchaseModal(currentOrganisationId)
                    }}
                  >
                    Show purchase requests
                  </Button>
                </div>
              }
            </>
          }
          
            {currentOrganisationId !== null && currentOrganisationId !== 0 && 
              <div style={{marginBottom: "16px"}}>
                <Button
                  sx={{
                    maxWidth: "240px",
                  }}
                  variant="contained"
                  startIcon={<GroupIcon sx={{color: '#ffffff'}} />}
                  onClick={() =>{
                    setShowCompanyUsers(true)
                  }}
                  >Manage company users
                </Button>
              </div>
            }

            {(
              currentOrganisation 
              && ((currentOrganisation.can_upload_validations === true && currentOrganisation.role === 'admin') || currentOrganisation.role === 'super-admin'))
            && <Button
              sx={{
                maxWidth: "290px",
                justifyContent: "flex-start",
              }}
              variant="contained"
              startIcon={<WhereToVoteIcon sx={{color: '#ffffff'}} />}
              onClick={() =>{
                setShowPrivateValidations(true)
              }}
              >Manage private validations</Button>
            }
          </>
        </CreateForm>
          
        {showCompanyUsers && 
          <OrganisationUsersModal
            open
            id={currentOrganisationId}
            handleClose={(forceClose: boolean) => {
              setShowCompanyUsers(false)
              if (forceClose) {
                refetchUserDetails()
                // Once the user leaves the org, this should close this modal
                handleClose()
              }
            }}
          />
        }
        {showPrivateValidations && 
          <ManagePrivateValidationsModal
            open
            id={currentOrganisationId}
            handleClose={() => {
              setShowPrivateValidations(false)
            }}
          />
        }
        
      </>
    )
}